import { useEffect, useState } from 'react';

import { RICHTEXT_SETTINGS } from './table.constants';
import { useRichtext } from 'approot/shared/richtext/richtext.hooks';
import { getRichtextAnswer } from '../shared/logic/get-richtext-answer';
import { useActivityAnswerId } from 'approot/activity-builder/present/activity-answers.hooks';
import { useActivityBuilderTheme } from 'approot/activity-builder/theme/activity-builder-theme';
import type { GetClassStudentAnswersQuery } from '../../present/activity-answers.graphql.generated';
import { TableCellEditor } from './table.style';
import { TableAnswer } from './table-view.constants';
import { getInitialContent } from 'approot/shared/richtext/get-initial-richtext-data';
import type { Pluck } from 'lib/gql/pluck';

const TableViewField = ({
  highlight,
  rowIndex,
  cellIndex,
  editorRowHeight,
  answer,
  updateAnswer,
  onFocus,
}: {
  highlight: boolean;
  rowIndex: number;
  cellIndex: number;
  editorRowHeight: number;
  answer: Pluck<GetClassStudentAnswersQuery, 'getClassStudentsAnswers'> | null;
  updateAnswer: (answer: TableAnswer) => void;
  onFocus?: () => void;
}) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { theme } = useActivityBuilderTheme();

  const id = useActivityAnswerId(answer);

  const { editor, toolbarRef } = useRichtext({
    initialValue: getInitialContent({
      initialValue: getRichtextAnswer(answer),
      settings: RICHTEXT_SETTINGS,
    }),
    onFocus: () => {
      setIsEditing(true);
      onFocus?.();
    },
    onBlur: () => setIsEditing(false),
    onChange: json =>
      updateAnswer({
        uuid: answer ? answer.uuid : id,
        answerType: 'text-answer',
        answerReferences: answer
          ? answer.answerReferences
          : {
              __typename: 'ReferenceProperties',
              id: null,
              row: rowIndex,
              col: cellIndex,
            },
        answer: {
          __typename: 'RichtextAnswer',
          answerType: 'text-answer',
          richtext: json,
        },
      }),
    settings: RICHTEXT_SETTINGS,
  });

  useEffect(() => {
    // answers come in separately, only initialise once
    if (answer && !isEditing) {
      const editorContent = getInitialContent({
        initialValue: getRichtextAnswer(answer),
        settings: RICHTEXT_SETTINGS,
      });
      editor?.commands.setContent(
        editorContent
          ? editorContent
          : {
              type: 'doc',
              content: [],
            }
      );
    }
  }, [answer, isEditing, editor]);

  return (
    editor && (
      <TableCellEditor
        editor={editor}
        toolbarRef={toolbarRef}
        $highlight={highlight}
        $baseFontSize={theme.font.medium.size}
        $baseLineHeight={theme.font.small.lineHeight}
        $rows={editorRowHeight}
      />
    )
  );
};

export default TableViewField;
